/**
 * Column spec + mechanical card-building helpers shared by the TikTok post widgets
 * (`PostsInlineList`, `RelatedPostsInlineList`). Each widget keeps its own model
 * builder (title, drift assertions); the declarative spec and the per-card assembly
 * — badge derivation and the column-aligned metric rows — are shared.
 */
import { TIKTOK_METADATA_BY_KEY } from 'utils/metrics';
import { shapePosts } from 'utils/api/tiktok.api';
import { getSentimentBadgeProps } from '@/ui/components/Post/utils';
import { tooltips } from '@/copy';
import { columnLabel, formatColumnValue } from './formatColumnValue';
import { growthTone } from './metricTone';
import type { ColumnSpec } from './tableHelpers';
import type {
    PostBadgesModel,
    PostCardModel,
} from '../primitives/InlinePostGrid';
import type { Column, EntityDataOutput } from '../types';

// Keys reference metrics.js so a rename there propagates here (and to parity with
// the dashboard posts section) instead of silently diverging.
const M = TIKTOK_METADATA_BY_KEY;

// Volume column is always views-latest-week (the figure the widget always shows).
// The sort key gets its own second column: `avg_weekly_views` ("last month") is the
// default sort and carries a per-post value, so the widget shows Views: Last Week +
// Avg Weekly Views by default — parity with the dashboard posts table (Option B: the
// sort surfaces its own metric column, as All Time surfaces Total Views). A volume
// metric, so no growth-tone arrow (same as the Total Views sort). The WoW growth
// fallback only renders when the sort IS the volume column (filtered out otherwise).
//
// Shared by both post widgets: related posts take the identical backend sort set and
// default (`TIKTOK_RELATED_POST_SORTS = TIKTOK_POST_SORTS`, `DEFAULT_RELATED_POST_SORT
// = DEFAULT_POST_SORT`). Split back into per-widget specs only if those diverge.
export const TIKTOK_POST_COLUMN_SPEC: ColumnSpec = {
    volumeKey: M.last_week_views.key,
    defaultSortKey: M.avg_weekly_views.key,
    volumeGrowthFallback: M.non_cum_WoW.key,
    sortableKeys: new Set<string>([
        M.last_week_views.key,
        M.avg_weekly_views.key,
        M.total_post_views.key,
        M.non_cum_WoW.key,
        M.sentiment_score.key,
        M.create_timestamp.key,
    ]),
    metaByKey: M,
};

// The card rows are tight (~⅓ of the widget) — use the short "Views" label there
// (dashboard-card precedent); the column header keeps the full metadata label.
export const cardRowLabel = (key: string): string =>
    key === M.last_week_views.key ? 'Views' : columnLabel(key, M);

/** A post from `shapePosts`: `_rows[i].data` flattened + the derived badge flags. */
export type ShapedPost = Record<string, unknown> & {
    url?: string;
    sentimentScoreInt?: number;
    sentiment_summary?: string;
    isNew?: boolean;
    isPaid?: boolean;
    isShop?: boolean;
    paidTypesMessage?: string;
};

export function postBadges(post: ShapedPost): PostBadgesModel {
    const score = post.sentimentScoreInt;
    // The dashboard's bucket icon ("Positive"/"Mixed"/"Negative" emotion face)
    // and score color drive the sentiment chip — no numeric score on it.
    const { Icon, fgColor, title } =
        score === undefined || score === null
            ? { Icon: undefined, fgColor: undefined, title: undefined }
            : getSentimentBadgeProps(score);
    return {
        sentiment: Icon
            ? { Icon, color: fgColor, title, summary: post.sentiment_summary }
            : undefined,
        isNew: !!post.isNew,
        isPaid: !!post.isPaid,
        paidTitle: post.paidTypesMessage,
        isShop: !!post.isShop,
        shopTitle: tooltips.isTikTokShop,
    };
}

/** One card per `_rows` post; metric rows follow the column order, so they read
 * like the header. */
export function buildPostCards(
    output: EntityDataOutput,
    columns: Column[],
): PostCardModel[] {
    const posts = shapePosts({ data: output?._rows ?? [] }) as ShapedPost[];
    const formattedRows = output?.result?.metrics;
    return posts.map((post, i): PostCardModel => {
        const rows = columns.map((col) => {
            const value = formatColumnValue(
                col.key,
                formattedRows?.[i],
                post,
                M,
            );
            return {
                label: cardRowLabel(col.key),
                value,
                tone: col.isGrowth ? growthTone(value) : undefined,
            };
        });
        return { url: post.url, badges: postBadges(post), rows };
    });
}
